import React, { useState } from 'react';
import type { FC } from 'react';
import { match } from 'variant';
import { I18nProvider, Trans } from '@wix/cli-i18n';
import type { Site } from '@wix/site-list-client';
import { Spinner, useAsync, useAsyncCallback } from '@wix/cli-ui-kit';
import { useDevCenterClient } from '@wix/dev-center-client';
import { useSiteListClient } from '@wix/site-list-client';
import type { BiLogger } from '@wix/cli-telemetry';
import { BiProvider } from '@wix/cli-telemetry-react';
import { SiteTypeSelect } from './SiteTypeSelect.js';
import type { SiteType } from './SiteTypeSelect.js';
import { CreateDevSite } from './CreateDevSite.js';
import { SelectExistingSite } from './SelectExistingSite.js';
import { HARMONY_DEVELOPMENT_SITE_TEMPLATE_ID } from './development-site.js';
import messages from './messages.json' with { type: 'json' };

interface Props {
  appId: string;
  biLogger: BiLogger;
  onSubmit: (site: Site) => void;
  onError: (error: unknown) => void;
}

export const SiteSelector: FC<Props> = ({
  appId,
  biLogger,
  onSubmit,
  onError,
}) => {
  const [siteType, setSiteType] = useState<SiteType | null>(null);
  const { createDevSite } = useDevCenterClient();
  const siteListClient = useSiteListClient();

  const { status } = useAsync(
    () =>
      siteListClient.querySites({
        query: {
          cursorPaging: {
            limit: 100,
          },
        },
      }),
    [],
    {
      onError,
    }
  );

  const createSite = useAsyncCallback(
    (_, siteType: SiteType) =>
      siteType === 'create-harmony-site'
        ? createDevSite({
            templateId: HARMONY_DEVELOPMENT_SITE_TEMPLATE_ID,
          })
        : createDevSite(),
    {
      onError,
    }
  );

  function handleSelectSiteType(siteType: SiteType) {
    setSiteType(siteType);

    if (siteType !== 'select-existing-site') {
      void createSite.execute(siteType);
    }
  }

  return (
    <BiProvider value={biLogger}>
      <I18nProvider messages={messages}>
        {match(status, {
          Error: () => null,
          Loading: () => <Spinner text={<Trans i18nKey="general.loading" />} />,
          Success: ({ result: sites }) => (
            <>
              <SiteTypeSelect
                onSubmit={handleSelectSiteType}
                onError={onError}
                sites={sites}
              />
              {siteType === 'select-existing-site' ? (
                <SelectExistingSite
                  sites={sites}
                  appId={appId}
                  onSubmit={onSubmit}
                  onError={onError}
                />
              ) : (
                <CreateDevSite
                  status={createSite.status}
                  appId={appId}
                  onSubmit={onSubmit}
                  onError={onError}
                />
              )}
            </>
          ),
        })}
      </I18nProvider>
    </BiProvider>
  );
};
